import React from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Shield } from 'lucide-react';

interface PermissionGuardProps {
  children: React.ReactNode;
  resource: string;
  fallback?: React.ReactNode;
}

export function PermissionGuard({ children, resource, fallback }: PermissionGuardProps) {
  const { hasPermissionSync, isOwner, contextStatus } = useWorkspace();

  // Show loading while context is loading
  if (contextStatus === 'loading') {
    return <div className="p-4">Loading...</div>;
  }

  // Owner bypass
  if (isOwner) return <>{children}</>;
  
  // Check permission
  if (hasPermissionSync(resource)) return <>{children}</>;
  
  // Show fallback or default access denied screen
  if (fallback) return <>{fallback}</>;
  
  return (
    <div className="min-h-[400px] flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-destructive" />
          </div>
          <CardTitle className="text-xl">Access Required</CardTitle>
          <CardDescription>
            You don't have permission to access {resource}. Contact your administrator for access.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <Button variant="outline" className="w-full">
            <Shield className="w-4 h-4 mr-2" />
            Request Access
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}